/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:  2023-07-26 08:00:56
 *
 *******************************************************/
//@flow

import _ from 'lodash';
import React, { Component } from 'react';
import { Icon, Button, Stack, TextField } from '@shopify/polaris';
import { MobileVerticalDotsMajor } from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import type { DropResult } from 'react-beautiful-dnd';
import { checkValidateSearchTermSuggestion } from 'src/helpers/instant-search-display-helper';

import { NUMBER_SEARCH_TERM_SUGGESTIONS } from 'src/constants/instant-search-display';
import TableCell from './table-cell';
import Delete from 'src/assets/images/common/delete_minor.svg';

type Props = {
  fields: Object,
  searchTermSuggestionsList: Array<string>
};

type ComposedProps = Props & WithI18nProps;
type State = {
  filterOption: Object,
  index: number,
  selectedId: number,
  addValue: string,
  error: string
};
// DONE
class SearchTermTable extends Component<ComposedProps, State> {
  state = {
    filterOption: {},
    index: -1,
    selectedId: -1,
    addValue: '',
    error: ''
  };

  onDragEnd = (result: DropResult) => {
    const { fields } = this.props;

    // dropped outside the list
    if (!result.destination || result.destination.index === result.source.index) {
      return;
    }

    // no movement
    if (result.destination.index === result.source.index) {
      return;
    }

    fields.move(result.source.index, result.destination.index);
  };

  render() {
    const { fields, searchTermSuggestionsList, i18n } = this.props;
    const { addValue, error } = this.state;

    return (
      <DragDropContext onDragEnd={this.onDragEnd}>
        <div className="Polaris-DataTable BC-DataTable__SearchTerm">
          <div className="BC__SearchTermSuggestion-Add">
            <Stack>
              <div onKeyDown={this.handleKeyPress}>
                <TextField
                  value={addValue}
                  onChange={this.handleChangeAddValue}
                  placeholder={i18n.translate(
                    'AdminHTML.InstantSearchDisplay.Common.searchTermTable_TextField_Placeholder'
                  )}
                  disabled={fields.length === NUMBER_SEARCH_TERM_SUGGESTIONS}
                  error={error}
                />
              </div>
              <Button
                primary
                onClick={this.handleAddValue}
                disabled={fields.length === NUMBER_SEARCH_TERM_SUGGESTIONS}>
                {i18n.translate('AdminHTML.Common.Action.add')}
              </Button>
            </Stack>
          </div>
          {_.isEmpty(fields) ? (
            <div className="empty-term">
              {i18n.translate('AdminHTML.InstantSearchDisplay.Common.searchTermTable_NoSearchTerm')}
            </div>
          ) : (
            <div className="Polaris-DataTable__ScrollContainer" style={{ marginLeft: 0 }}>
              <table className="Polaris-DataTable__Table">
                <Droppable droppableId="searchTermTable">
                  {provided => (
                    <tbody ref={provided.innerRef} {...provided.droppableProps}>
                      {fields.map((item, key) => {
                        return (
                          // Enable drag for per option row
                          <Draggable draggableId={`${item}-${key}`} key={item} index={key}>
                            {(provided, snapshot) => (
                              <tr
                                className="Polaris-DataTable__TableRow"
                                ref={provided.innerRef}
                                {...provided.dragHandleProps}
                                {...provided.draggableProps}>
                                {/* Drag icon */}
                                <TableCell
                                  isDragOccurring={snapshot.isDragging}
                                  className="Polaris-DataTable__Cell Polaris-DataTable__Cell--firstColumn">
                                  <Icon color="subdued" source={MobileVerticalDotsMajor} />
                                </TableCell>

                                {/* Content */}
                                <TableCell
                                  isDragOccurring={snapshot.isDragging}
                                  className="Polaris-DataTable__Cell">
                                  <p>{searchTermSuggestionsList[key]}</p>
                                </TableCell>

                                {/* Action */}
                                <TableCell
                                  isDragOccurring={snapshot.isDragging}
                                  className="Polaris-DataTable__Cell Polaris-DataTable__Cell-Action">
                                  <Button
                                    size="slim"
                                    icon={<img src={Delete} alt="delete" />}
                                    onClick={() => {
                                      this.handleRemove(key);
                                    }}
                                  />
                                </TableCell>
                              </tr>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </tbody>
                  )}
                </Droppable>
              </table>
            </div>
          )}
        </div>
      </DragDropContext>
    );
  }

  handleRemove = key => {
    const { fields } = this.props;

    fields.remove(key);
  };

  // Handle change add value
  handleChangeAddValue = value => {
    this.setState({
      addValue: value,
      error: ''
    });
  };

  // Handle when the enter key is press in Add input
  handleKeyPress = event => {
    const enterKeyPressed = event.keyCode === 13;
    if (enterKeyPressed) {
      event.preventDefault();
      this.handleAddValue();
    }
  };

  // Handle add value
  handleAddValue = () => {
    const { fields, searchTermSuggestionsList, i18n } = this.props;
    let { addValue } = this.state;

    const checkedValue = checkValidateSearchTermSuggestion(
      addValue,
      searchTermSuggestionsList,
      i18n
    );
    if (checkedValue.error) {
      this.setState({
        error: checkedValue.error
      });
    } else {
      // $FlowFixMe
      const { searchTermSuggestion = '' } = checkedValue;
      if (searchTermSuggestion) {
        fields.push(searchTermSuggestion);
      }
      this.setState({
        addValue: '',
        error: ''
      });
    }
  };
}
export default withI18n()(SearchTermTable);
